import React from 'react';
import { Platform } from 'react-native';
import { CommonActions, useNavigation } from '@react-navigation/native';

import type {
  AuthRedirectTarget,
  AuthStackParamList,
} from '../../navigation/AppNavigation.props';
import { authRedirectPathFromTarget } from '../../navigation/AppNavigation.props';
import { useAuthFlowContext } from './AuthFlow.context';

export const useAuthFlow = () => {
  const { authRedirectTarget, setAuthRedirectTarget } = useAuthFlowContext();
  const navigation = useNavigation();

  const clearAuthRedirectTarget = React.useCallback(() => {
    setAuthRedirectTarget(undefined);
  }, [setAuthRedirectTarget]);

  const startAuthentication = React.useCallback(
    (
      screen: keyof Pick<AuthStackParamList, 'Login' | 'Join'>,
      redirectTarget?: AuthRedirectTarget,
    ) => {
      setAuthRedirectTarget(redirectTarget);
      navigation.navigate('Auth', {
        screen,
        params: redirectTarget ? { redirect: redirectTarget } : {},
      });
    },
    [navigation, setAuthRedirectTarget],
  );

  const startSignIn = React.useCallback(
    (redirectTarget?: AuthRedirectTarget) => {
      startAuthentication('Login', redirectTarget);
    },
    [startAuthentication],
  );

  const startJoin = React.useCallback(
    (redirectTarget?: AuthRedirectTarget) => {
      startAuthentication('Join', redirectTarget);
    },
    [startAuthentication],
  );

  const redirectAfterSignIn = React.useCallback(
    (redirectParam?: AuthRedirectTarget) => {
      setAuthRedirectTarget((target) => {
        const path = authRedirectPathFromTarget(redirectParam ?? target);

        if (path) {
          if (Platform.OS === 'web' && path.screen === 'MainTabs') {
            // Workaround for the web-specific issue where navigating to MainTabs from a modal
            // results in an incorrect address bar URL.
            // We first dismiss all modal screens (Auth, possibly Modal) by resetting the root stack,
            // then perform the navigation after a timeout, otherwise calls may be debounced.
            navigation.getParent()?.dispatch((state) => {
              return CommonActions.reset({
                ...state,
                routes: state.routes.slice(0, 1),
                index: 0,
              });
            });
            setTimeout(() => {
              navigation.navigate(path.screen, path.params);
            });
          } else {
            navigation.navigate(path.screen, path.params);
          }
        } else {
          navigation.getParent()?.goBack();
        }

        return undefined;
      });
    },
    [navigation, setAuthRedirectTarget],
  );

  const value = React.useMemo(
    () => ({
      authRedirectTarget,
      setAuthRedirectTarget,
      clearAuthRedirectTarget,
      startSignIn,
      startJoin,
      redirectAfterSignIn,
    }),
    [
      authRedirectTarget,
      clearAuthRedirectTarget,
      setAuthRedirectTarget,
      startSignIn,
      startJoin,
      redirectAfterSignIn,
    ],
  );

  return value;
};
